import {
    getStore as getMailListLayoutStore,
    isReadingPanePositionRight,
    type MailListColumnHeaderType,
    getColumnMinWidth,
} from 'owa-mail-layout';
import type { ListViewColumnHeadersOptions } from 'owa-outlook-service-option-store';
import { getDefaultOptions } from 'owa-outlook-service-options';
import { getOptionsForFeature, OwsOptionsFeatureType } from 'owa-outlook-service-option-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import { setItem } from 'owa-local-storage';
import {
    MCL_LIST_ITEM_RIGHT_PADDING,
    MCL_RPRIGHT_LIST_ITEM_RIGHT_PADDING,
} from 'owa-mail-layout/lib/mailLayoutConstants';

export default function calculateColumnWidths(
    isInitialLayout: boolean,
    containerWidth: number,
    prevContainerWidth: React.MutableRefObject<number | null>
): {
    senderColumnWidth: number | null;
    subjectColumnWidth: number | null;
    receivedColumnWidth: number | null;
} {
    /**
     * We should only do this resize work if the width of the container
     * has changed (or it's the initial layout).
     */
    if (!isInitialLayout && prevContainerWidth?.current === containerWidth) {
        return getCurrentColumnWidths();
    }
    const shouldShowColumnHeaderStyles =
        isFeatureEnabled('tri-mlFocusSelect') && isFeatureEnabled('tri-mlColumnHeaderHover');
    prevContainerWidth.current = containerWidth;

    const preferredColumnWidths = getPreferredColumnWidths();

    const isReadingPaneRight = isReadingPanePositionRight();
    const senderColumnMinWidth = getColumnMinWidth(0);
    const subjectColumnMinWidth = getColumnMinWidth(1);
    const receivedColumnMinWidth = getColumnMinWidth(2);

    // We need to make sure the columns are at least as wide as their min width.
    const adjustedSenderColumnWidth = Math.max(
        preferredColumnWidths.senderColumnWidth,
        senderColumnMinWidth
    );
    const adjustedSubjectColumnWidth = Math.max(
        preferredColumnWidths.subjectColumnWidth,
        subjectColumnMinWidth
    );
    const adjustedReceivedColumnWidth = Math.max(
        preferredColumnWidths.receivedColumnWidth,
        receivedColumnMinWidth
    );

    /**
     * Calculates the remaining space in the container after the columns,
     * resize handles, and extra space is accounted for.
     */
    const resizeHandleWidth = shouldShowColumnHeaderStyles ? 2 : 32;
    const senderColumnmHeaderMarginLeft = 59;
    const mlRowMarginRight = isReadingPaneRight
        ? MCL_RPRIGHT_LIST_ITEM_RIGHT_PADDING
        : MCL_LIST_ITEM_RIGHT_PADDING;
    const mlRowMargins = mlRowMarginRight + senderColumnmHeaderMarginLeft;

    const remainingSpace =
        containerWidth -
        adjustedSenderColumnWidth -
        adjustedSubjectColumnWidth -
        adjustedReceivedColumnWidth -
        resizeHandleWidth -
        (!shouldShowColumnHeaderStyles ? mlRowMargins : 0);

    /** Keep the ratio between the sender and subject column widths the same
     * as the user increases  and decreases the width of the entire window.
     * The sender to subject column ratio will always be calculated from the preferred
     * or minimum width
     */
    const totalWidth = adjustedSenderColumnWidth + adjustedSubjectColumnWidth;
    const senderProportion = totalWidth > 0 ? adjustedSenderColumnWidth / totalWidth : 0;
    const subjectProportion = totalWidth > 0 ? adjustedSubjectColumnWidth / totalWidth : 0;

    let newSenderColumnWidth = adjustedSenderColumnWidth;
    let newSubjectColumnWidth = adjustedSubjectColumnWidth;
    let newReceivedColumnWidth = adjustedReceivedColumnWidth;

    /**
     * If the value is positive, that means there's extra space we can
     * use to increase the column sizes. We allocate space in a way that
     * maintains the ratio between the sender and subject columns
     */
    if (remainingSpace > 0) {
        const spaceToAllocate = Math.floor(remainingSpace);
        newSenderColumnWidth = newSenderColumnWidth + spaceToAllocate * senderProportion;
        newSubjectColumnWidth = newSubjectColumnWidth + spaceToAllocate * subjectProportion;
        // Received Column Header extends to end of container. Adjust its width so that it fills remaining space.
        newReceivedColumnWidth = shouldShowColumnHeaderStyles
            ? containerWidth - newSenderColumnWidth - newSubjectColumnWidth
            : newReceivedColumnWidth;
    }

    /**
     * If the value is negative, that means we need to reduce the column
     * sizes so they can fit.
     */
    if (remainingSpace < 0) {
        let spaceToTrim = Math.floor(Math.abs(remainingSpace));

        // First, trim the received column as much as possible without going below its min width
        const receivedColumnTrimSpace = Math.max(
            0,
            newReceivedColumnWidth - receivedColumnMinWidth
        );
        const receivedColumnTrimAmount = Math.min(spaceToTrim, receivedColumnTrimSpace);
        newReceivedColumnWidth = newReceivedColumnWidth - receivedColumnTrimAmount;
        spaceToTrim = spaceToTrim - receivedColumnTrimAmount;

        // Apply remaining space reduction to sender and subject columns based on their proportions
        if (spaceToTrim > 0) {
            newSenderColumnWidth = newSenderColumnWidth - spaceToTrim * senderProportion;
            newSubjectColumnWidth = newSubjectColumnWidth - spaceToTrim * subjectProportion;

            if (newSenderColumnWidth < senderColumnMinWidth) {
                // Since we can't reduce the sender column width below the min width, we adjust the subject column width
                const diff = senderColumnMinWidth - newSenderColumnWidth;
                newSubjectColumnWidth = newSubjectColumnWidth - diff;
                newSenderColumnWidth = senderColumnMinWidth;
            }

            if (
                newSubjectColumnWidth < subjectColumnMinWidth &&
                newSenderColumnWidth > senderColumnMinWidth
            ) {
                // Since we can't reduce the subject column width below the min width, we adjust the sender column width
                const diff = subjectColumnMinWidth - newSubjectColumnWidth;
                newSenderColumnWidth = newSenderColumnWidth - diff;
                newSubjectColumnWidth = subjectColumnMinWidth;
            }
        }
    }

    updateLocalColumnWidths(newSenderColumnWidth, newSubjectColumnWidth, newReceivedColumnWidth);

    return {
        senderColumnWidth: newSenderColumnWidth,
        subjectColumnWidth: newSubjectColumnWidth,
        receivedColumnWidth: newReceivedColumnWidth,
    };
}

const getPreferredColumnWidths = () => {
    /**
     * Fetch the user preferences from the server. If no preferences are found,
     * fall back to the default values for these settings.
     */
    const listViewColumnHeadersOptions =
        getOptionsForFeature<ListViewColumnHeadersOptions>(
            OwsOptionsFeatureType.ListViewColumnHeaders
        ) ||
        (getDefaultOptions()[
            OwsOptionsFeatureType.ListViewColumnHeaders
        ] as ListViewColumnHeadersOptions);

    return {
        senderColumnWidth: listViewColumnHeadersOptions.senderColumnWidth,
        subjectColumnWidth: listViewColumnHeadersOptions.subjectColumnWidth,
        receivedColumnWidth: listViewColumnHeadersOptions.receivedColumnWidth,
    };
};

const getCurrentColumnWidths = () => {
    const { senderColumnWidth, subjectColumnWidth, receivedColumnWidth } = getMailListLayoutStore();

    return {
        senderColumnWidth,
        subjectColumnWidth,
        receivedColumnWidth,
    };
};

// This function stores the adjusted column widths (i.e. the user's preferred column
// widths + any adjustments made to fit the columns within the available space) in
// local storage so that we can render the columns at the appropriate width the
// next time the user reloads the page.
const updateLocalColumnWidths = (
    senderColumnWidth: number,
    subjectColumnWidth: number,
    receivedColumnWidth: number
) => {
    setItem(
        window,
        `MailListColumnWidths`,
        JSON.stringify({
            senderColumnWidth,
            subjectColumnWidth,
            receivedColumnWidth,
        })
    );
};
